import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Modal, Button, Dropdown } from 'react-bootstrap';
import './EditOrderModal.css';
import TauriLoader2 from '../../TauriLoader2/TauriLoader2';
import appURL from '../../../appURL.json';
import { addRecord, deleteRecord, getAll, getNextId, getRecordById, updateRecord } from '../../../localDB';
import OpalSearchInput from '../../OpalSearchInput/OpalSearchInput';
import { accountMapping, orderStatus } from '../../../visualizations/visualizations';
import { Input, Label } from 'reactstrap';
import { transformOrder, transformOrderDetails, upsertLocalDataOrderDetails, upsertLocalDataOrders } from '../OrderFactoryData/OrderFactoryData';
import OpalDataTable from '../../OpalDataTable/OpalDataTable';
import OpalAddItemRequestModal from '../../OpalCoverageLayout/CoverageView/OpalOrderRequest/OpalAddItemRequestModal/OpalAddItemRequestModal';
import OpalAlertModal from '../../OpalAlertModal/OpalAlertModal';
import SubmitOrderModal from '../SubmitOrderModal/SubmitOrderModal';
import config from '../../../config.json';
import OrderAddAuthorizerModal from '../OrderAddAuthorizerModal/OrderAddAuthorizerModal';

const EditOrderModal = ({ show, onHide, isCoverage = false, id }) => {

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [remarks, setRemarks] = useState('');
    const [authorizer, setAuthorizer] = useState('');
    const [order, setOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [loadingOrder, setLoadingOrder] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const [items, setItems] = useState([]);
    const [showItemModal, setShowItemModal] = useState(false);
    const [showAuthorizerModal, setShowAuthorizerModal] = useState(false);

    const [loadingDetails, setLoadingDetails] = useState(false);
    const [isAddingItem, setIsAddingItem] = useState(false);

    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertModalProps, setAlertModalProps] = useState(null);
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    const [showSubmitModal, setShowSubmitModal] = useState(false);

    const orderStatusName = Object.fromEntries(
        Object.entries(orderStatus).map(([key, value]) => [value, key])
    );

    const orderDetailColumns = [
        { key: "Item.Code", label: "CODE" },
        { key: "Item.Description", label: "DESCRIPTION" },
        { key: "Unit.Description", label: "UNIT" },

        {
            key: "DetailPrice",
            label: "DETAIL PRICE",
            editable: true,
            inputType: "number",
            onBlur: (row, value) =>
                handleUpdateField(row.ID, "DetailPrice", Number(value))
        },
        {
            key: "Quantity",
            label: "QUANTITY",
            editable: true,
            inputType: "number",
            onBlur: (row, value) =>
                handleUpdateField(row.ID, "Quantity", Number(value))
        },
        {
            key: "FreeGoods",
            label: "FREE GOODS",
            editable: true,
            inputType: "number",
            onBlur: (row, value) =>
                handleUpdateField(row.ID, "FreeGoods", Number(value))
        },
        {
            key: "DiscountPercent",
            label: "DISCOUNT %",
            editable: true,
            inputType: "number",
            onBlur: (row, value) =>
                handleUpdateField(row.ID, "DiscountPercent", Number(value))
        },

        {
            key: "NetLineTotal",
            inputType: "number",
            label: "TOTAL"
        },
        {
            key: "Remarks",
            label: "REMARKS",
            editable: true,
            onBlur: (row, value) =>
                handleUpdateField(row.ID, "Remarks", value)
        }
    ];

    const orderDetailsActions = [
        {
            label: "Delete",
            danger: true,
            onClick: row => handleRemoveOrderDetails(row.ID)
        }
    ];

    let lastTimestamp = 0;
    let sequence = 0;

    const generateId = () => {
        const now = Date.now();

        if (now === lastTimestamp) {
            sequence++;
        } else {
            sequence = 0;
            lastTimestamp = now;
        }
        if (sequence >= 1000) {
            throw new Error("Too many IDs generated in the same millisecond");
        }
        return now * 1000 + sequence;
    };

    const handleRowClick = (accID) => {
        if (order?.Status !== orderStatus.Draft) return;
    };

    const handleUpdateField = async (detailId, field, value) => {
        if (order?.Status !== orderStatus.Draft) return;

        const detail = await getRecordById("OrderDetails", detailId);
        if (!detail) return;

        const updatedDetail = {
            ...detail,
            [field]: value
        };

        const price = Number(updatedDetail.DetailPrice) || 0;
        const qty = Number(updatedDetail.Quantity) || 0;
        const discountPct = Number(updatedDetail.DiscountPercent) || 0;

        const gross = Number((price * qty).toFixed(config.DISPLAYED_DECIMALS));
        const discountVal = Number(((discountPct / 100) * gross).toFixed(config.DISPLAYED_DECIMALS));
        const net = Number((gross - discountVal).toFixed(config.DISPLAYED_DECIMALS));

        updatedDetail.GrossLineTotal = gross;
        updatedDetail.DiscountValue = discountVal;
        updatedDetail.NetLineTotal = net;

        setOrderItems(prev =>
            prev.map(item => (item.ID === detailId ? updatedDetail : item))
        );

        await updateRecord("OrderDetails", updatedDetail);

        if (isCoverage) return;

        const payload = {
            OrderID: order?.ID,
            ItemID: updatedDetail.Item.ID,
            UnitID: updatedDetail.Unit.ID,
            DetailPrice: Number(updatedDetail.DetailPrice) || 0,
            Quantity: Number(updatedDetail.Quantity) || 0,
            FreeGoods: Number(updatedDetail.FreeGoods) || 0,
            DiscountPercent: Number(updatedDetail.DiscountPercent) || 0,
            Remarks: updatedDetail.Remarks || ""
        };

        handleUpdateOrderDetails(detailId, payload);
    };


    const handleAddSelected = async (selectedItems) => {
        if (!selectedItems?.length || !id) return;

        try {
            setIsAddingItem(true);

            for (const item of selectedItems) {
                const payload = {
                    OrderID: id,
                    ItemID: item.ID,
                    UnitID: item.UnitID,
                    DetailPrice: item.DefaultPrice,
                    Quantity: 1,
                    FreeGoods: 0,
                    DiscountPercent: 0,
                    Remarks: ""
                };

                await handleCreateOrderDetails(payload);
            }

            await fetchOrderDetails(id);
            setShowItemModal(false);
        } catch (error) {
            console.error('Error adding items:', error);
        } finally {
            setIsAddingItem(false);
        }
    };


    const fetchAccount = async () => {
        try {
            const acc = await getAll("customer");
            const accFilter = acc.filter(a =>
                a.Type === accountMapping.Customer || a.Type === accountMapping.Doctor
            );

            setAccounts(accFilter);
        } catch {
            return
        }
    }

    const fetchOrder = async (OrderID = id) => {
        setLoadingOrder(true);
        try {
            const it = await getAll("item");

            if (isCoverage) {
                const o = await getRecordById("Orders", OrderID);
                const od = await getAll("OrderDetails");
                const order_items = od.filter(a => a.OrderID === OrderID);


                setOrder(o);
                setOrderItems(order_items);
                setItems(it);
                return;
            }

            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_GetOrders}/id/${OrderID}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                }
            });

            if (!response.ok) {
                console.error(response);
                setLoadingOrder(false);
                return;
            }

            const data = await response.json();
            const transformedOrder = await transformOrder(data.order || null);
            const transformedOrderItems = await transformOrderDetails(data?.details || [], OrderID);

            setOrder(transformedOrder);
            setOrderItems(transformedOrderItems);
            setItems(it);


            await upsertLocalDataOrders(
                transformedOrder ? [transformedOrder] : []
            );
            await upsertLocalDataOrderDetails(transformedOrderItems);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingOrder(false);
        }
    };

    const fetchOrderDetails = async (OrderID) => {
        try {
            if (isCoverage) {
                const od = await getAll("OrderDetails");
                const order_items = od.filter(a => a.OrderID === OrderID);

                setOrderItems(order_items);

                return;
            }

            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_GetOrders}/${OrderID}/details`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                }
            });

            if (!response.ok) {
                console.error(response);
                return;
            }

            const data = await response.json();
            const transformedOrderItems = await transformOrderDetails(data?.details || [], OrderID);

            setOrderItems(transformedOrderItems);
            await upsertLocalDataOrderDetails(transformedOrderItems);

        } catch (error) {
            console.log(error);
        }
    };

    const handleEditOrderHeader = async (payload) => {

        if (order?.Status !== orderStatus.Draft || !payload) return;

        try {
            setIsUpdating(true);

            if (!isCoverage) {
                const token = localStorage.getItem('token');
                const devId = localStorage.getItem('devId');

                const response = await fetch(`${appURL.API_UpdateOrders}/${id}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-ETR-DeviceID': devId
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    console.error('Failed to update order');
                    return;
                }
            }

            updateOrderInfo();
            setHasChanges(true);
        } catch (error) {
            console.log(error);
        } finally {
            setIsUpdating(false);
        }
    }

    const handleAuthorizerChange = async (value) => {
        const o = await getRecordById("Orders", id);
        const updateAuthorizer = {
            ...o,
            Authorizer: value
        };
        await updateRecord("Orders", updateAuthorizer);

        setAuthorizer(value);
        const payload = {
            AccountID: selectedAccount?.ID,
            DeliveryAddress: deliveryAddress,
            Remarks: remarks,
            Authorizer: value
        }

        handleEditOrderHeader(payload);
    }

    const handleCreateOrderDetails = async (payload) => {
        if (order?.Status !== orderStatus.Draft || !payload) return;

        try {

            if (isCoverage) {
                const order_items = await getAll("OrderDetails");

                const od = order_items.find(a =>
                    a.OrderID === payload.OrderID &&
                    a.Item.ID === payload.ItemID &&
                    a.Unit.ID === payload.UnitID
                );

                if (od) return;

                const combinedId = generateId();

                const item = await getRecordById("item", payload.ItemID);
                const unit = await getRecordById("unit", payload.UnitID);

                const { ItemID, UnitID, ...restPayload } = payload;

                const localPayload = {
                    ...restPayload,
                    ID: combinedId,
                    Item: {
                        ID: item?.ID,
                        Code: item?.Code,
                        Description: item?.Description,
                    },
                    Unit: {
                        ID: unit?.ID,
                        Description: unit?.Description,
                    },
                };

                await addRecord("OrderDetails", localPayload);
                setHasChanges(true);
                return;
            }

            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_CreateOrderDetails}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Failed to create order detail');
                return;
            }

            const data = await response.json();
            setHasChanges(true);

        } catch (error) {
            console.log(error);
        }
    }

    const handleUpdateOrderDetails = async (detailId, payload) => {
        if (order?.Status !== orderStatus.Draft || !payload) return;

        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_UpdateOrderDetails}/${detailId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Failed to update order detail');
                return;
            }

            const data = await response.json();
            setHasChanges(true);

        } catch (error) {
            console.log(error);
        }
    }

    const handleRemoveOrderDetails = async (detailId) => {
        if (order?.Status !== orderStatus.Draft) return;
        setShowLoadingModal(true);
        try {
            if (isCoverage) {
                await deleteRecord("OrderDetails", detailId);
                await fetchOrderDetails(id);
                setHasChanges(true);
                return;
            }

            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_RemoveOrderDetails}/${detailId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                }
            });

            if (!response.ok) {
                console.error('Failed to remove order detail');
                return;
            }

            const data = await response.json();
            await fetchOrderDetails(id);
            setHasChanges(true);

        } catch (error) {
            console.log(error);
        } finally {
            setShowLoadingModal(false);
        }
    }

    const initializedOrderData = async () => {
        const o = await getRecordById("customer", order?.Account?.ID);

        setSelectedAccount(o);
        setDeliveryAddress(order?.DeliveryAddress);
        setRemarks(order?.Remarks);
        setAuthorizer(order?.Authorizer);
    }

    useEffect(() => {
        if (order && accounts.length > 0) {
            initializedOrderData();
        }
    }, [order, accounts]);

    const getPayload = () => ({
        AccountID: selectedAccount?.ID,
        DeliveryAddress: deliveryAddress,
        Remarks: remarks,
        Authorizer: authorizer
    });

    useEffect(() => {
        if (id) {
            fetchAccount();
            fetchOrder(id);
        }
    }, [id]);

    const updateOrderToSubmitted = async (OrderId = id) => {
        const _order = await getRecordById("Orders", OrderId);

        const updatedOrder = {
            ..._order,
            Status: orderStatus.Submitted
        }

        await updateRecord("Orders", updatedOrder);
        setOrder(updatedOrder);
    }

    const handleSubmit = async (sign) => {
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            const formData = new FormData();
            formData.append("id", String(id)); // ensure string

            if (sign?.ImageData) {
                formData.append("sign_img", sign.ImageData, sign.FileName);
            }

            const response = await fetch(appURL.API_OrderSubmit, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "X-ETR-DeviceID": devId
                },
                body: formData
            });

            if (!response.ok) {
                const text = await response.text();
                console.error("Submit failed:", text);
                return;
            }

            await updateOrderToSubmitted();
            onHide(hasChanges);
            setShowSubmitModal(false);

        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleClose = () => {
        if (onHide) onHide(hasChanges);
        setHasChanges(false);
    }

    const updateOrderInfo = async (OrderId = id) => {
        const _order = await getRecordById("Orders", OrderId);

        const updatedOrder = {
            ..._order,
            Account: {
                ID: selectedAccount?.ID,
                Name: selectedAccount?.Name
            },
            DeliveryAddress: deliveryAddress,
            Remarks: remarks
        }

        await updateRecord("Orders", updatedOrder);
        setOrder(updatedOrder);
    }

    const updateOrderTotal = async () => {
        if (!orderItems || orderItems.length === 0) return;

        const totals = orderItems.reduce(
            (acc, item) => {
                acc.GrossTotal += Number(item.GrossLineTotal) || 0;
                acc.DiscountTotal += Number(item.DiscountValue) || 0;
                acc.NetTotal += Number(item.NetLineTotal) || 0;
                return acc;
            },
            { GrossTotal: 0, DiscountTotal: 0, NetTotal: 0 }
        );

        const localOrder = await getRecordById("Orders", id);
        if (!localOrder) return;

        const updatedOrder = {
            ...localOrder,
            GrossTotal: Number(totals.GrossTotal.toFixed(config.DISPLAYED_DECIMALS)),
            TotalDiscount: Number(totals.DiscountTotal.toFixed(config.DISPLAYED_DECIMALS)),
            NetTotal: Number(totals.NetTotal.toFixed(config.DISPLAYED_DECIMALS))
        };

        await updateRecord("Orders", updatedOrder);
    };

    useEffect(() => {
        updateOrderTotal();
    }, [orderItems]);

    const totals = useMemo(() => {
        return orderItems?.reduce(
            (acc, item) => {
                acc.GrossTotal += item.GrossLineTotal ?? 0;
                acc.DiscountTotal += item.DiscountValue ?? 0;
                acc.NetTotal += item.NetLineTotal ?? 0;
                return acc;
            },
            { GrossTotal: 0, DiscountTotal: 0, NetTotal: 0 }
        ) || { GrossTotal: 0, DiscountTotal: 0, NetTotal: 0 };
    }, [orderItems]);

    const { GrossTotal, DiscountTotal, NetTotal } = totals;

    const formatNumber = (value) =>
        Number(value || 0).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: config.DISPLAYED_DECIMALS
        });


    return (
        <Modal
            className='edit-order-modal'
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            backdrop={isSubmitting ? "static" : true}
            keyboard={!isSubmitting}
        >
            <Modal.Header>Order Placement</Modal.Header>

            <Modal.Body>
                {loadingOrder ?
                    (
                        <TauriLoader2
                            width="2.5rem"
                            height="2.5rem"
                            borderwidth="0.35rem"
                            color="#1D5D9B"
                        />
                    ) : (
                        <Accordion defaultActiveKey={['0', '1']} alwaysOpen>

                            {/* ================= ORDER HEADER ================= */}
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>
                                    ORDER INFORMATION
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="order-header">

                                        <div className="row g-3 mb-1">
                                            <div className="col">
                                                <Label>Order No</Label>
                                                <Input type="text" value={order?.OrderNumber} readOnly />
                                            </div>
                                            <div className="col">
                                                <Label>Order Date</Label>
                                                <Input type="text" value={order?.OrderDate} readOnly />
                                            </div>
                                            <div className="col">
                                                <Label>Status</Label>
                                                <Input type="text" value={orderStatusName[order?.Status] ?? '--'} readOnly />
                                            </div>
                                        </div>


                                        <div className="row g-2" style={{ height: '100%' }}>
                                            <div className="col-md-6 d-flex flex-column">
                                                <div className="mb-2">
                                                    <Label>Account</Label>
                                                    <OpalSearchInput
                                                        selectedItem={selectedAccount}
                                                        onSelectedItem={(item) => {
                                                            setSelectedAccount(item);
                                                            setDeliveryAddress(item.AddressDetail || '');
                                                            setHasChanges(true);
                                                            handleEditOrderHeader({
                                                                AccountID: item.ID,
                                                                DeliveryAddress: item.AddressDetail,
                                                                Remarks: remarks
                                                            });
                                                        }}
                                                        items={accounts}
                                                    />
                                                </div>

                                                <div className="d-flex flex-column flex-grow-1">
                                                    <Label>Delivery Address</Label>
                                                    <Input
                                                        type="textarea"
                                                        className="py-1 flex-grow-1"
                                                        style={{ resize: 'none', minHeight: '5rem' }}
                                                        value={deliveryAddress}
                                                        onChange={e => {
                                                            setDeliveryAddress(e.target.value);
                                                            setHasChanges(true);
                                                        }}
                                                        onBlur={() => handleEditOrderHeader(getPayload())}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6 d-flex flex-column">
                                                <Label>Remarks</Label>
                                                <div className="flex-grow-1 d-flex flex-column">
                                                    <Input
                                                        type="textarea"
                                                        className="py-1 flex-grow-1"
                                                        style={{ resize: 'none' }}
                                                        value={remarks}
                                                        onChange={e => {
                                                            setRemarks(e.target.value);
                                                            setHasChanges(true);
                                                        }}
                                                        onBlur={() => handleEditOrderHeader(getPayload())}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* ================= ORDER DETAILS ================= */}
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>
                                    ORDER ITEMS
                                </Accordion.Header>

                                <Accordion.Body>
                                    <div className="order-details">
                                        <div className={`w-100 d-flex ${selectedAccount?.Type === accountMapping.Customer ? "justify-content-between" : "justify-content-end"}  mb-2`}>
                                            {selectedAccount?.Type === accountMapping.Customer &&
                                                <Button
                                                    className="btn-add d-flex"
                                                    onClick={() => setShowAuthorizerModal(true)}
                                                    disabled={order?.Status !== orderStatus.Draft}
                                                >
                                                    <span className="text-nowrap">Authorize Person</span>
                                                </Button>
                                            }
                                            <Button
                                                className="btn-add d-flex"
                                                onClick={() => setShowItemModal(true)}
                                                disabled={order?.Status !== orderStatus.Draft}
                                            >
                                                <span className="text-nowrap">Add Item</span>
                                            </Button>
                                        </div>
                                        <div className="order-table-wrapper">
                                            <OpalDataTable
                                                loading={loadingDetails}
                                                data={orderItems}
                                                columns={orderDetailColumns}
                                                actions={orderDetailsActions}
                                                onRowClick={handleRowClick}
                                                isDisabled={order?.Status !== orderStatus.Draft}
                                            />
                                        </div>

                                        <div className="order-totals d-flex align-items-center justify-content-end gap-4 mt-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <Label className='order-total-label'>GROSS AMOUNT:</Label>
                                                <Label className="order-total-value">
                                                    {formatNumber(GrossTotal)}
                                                </Label>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Label className='order-total-label'>DISCOUNT AMOUNT:</Label>
                                                <Label className="order-total-value">
                                                    {formatNumber(DiscountTotal)}
                                                </Label>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Label className='order-total-label'>NET AMOUNT:</Label>
                                                <Label className="order-total-value">
                                                    {formatNumber(NetTotal)}
                                                </Label>
                                            </div>
                                        </div>
                                    </div>

                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
            </Modal.Body>



            <Modal.Footer className="justify-content-end">
                {!isCoverage &&
                    <Button className='btn-submit d-flex align-items-center' disabled={isSubmitting || order?.Status !== orderStatus.Draft} onClick={() => setShowSubmitModal(true)}>
                        {isSubmitting && (
                            <TauriLoader2 width="1.2rem" height="1.2rem" borderwidth="0.2rem" color="#1D5D9B" />
                        )}
                        <span className="text-nowrap">Send Order</span>
                    </Button>
                }
                <Button className="btn-cancel d-flex" onClick={onHide}>
                    <span className="text-nowrap">Close</span>
                </Button>
            </Modal.Footer>

            <OpalAddItemRequestModal
                show={showItemModal}
                onHide={() => setShowItemModal(false)}
                data={items}
                onAddSelected={handleAddSelected}
                loading={isAddingItem}
            />
            <OrderAddAuthorizerModal
                show={showAuthorizerModal}
                onHide={() => setShowAuthorizerModal(false)}
                display={authorizer}
                onUpdate={handleAuthorizerChange}
            />
            <OpalAlertModal
                show={showAlertModal}
                onHide={() => setShowAlertModal(false)}
                title={alertModalProps?.title}
                bodyText={alertModalProps?.bodyText}
                confirmButtonText={alertModalProps?.confirmButtonText}
                cancelButtonText={alertModalProps?.cancelButtonText}
                onConfirm={alertModalProps?.onConfirm}
                modalType={alertModalProps?.modalType}
                Icon={alertModalProps?.Icon}
            />
            <OpalAlertModal
                show={showLoadingModal}
                onHide={() => setShowLoadingModal(false)}
                modalType={"message"}
                Icon={"/Images/loader.svg"}
                backdrop={"static"}
                keyboard={false}
                hideFooter={true}
            />
            <SubmitOrderModal
                show={showSubmitModal}
                onHide={() => setShowSubmitModal(false)}
                isSubmitting={isSubmitting}
                onSubmitting={handleSubmit}
                id={id}
            />
        </Modal >
    );
};

export default EditOrderModal;
